import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import natashaImg from "@/assets/natasha-profile.webp";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { usePreferredMeasureUnit } from "@/hooks/usePreferredMeasureUnit";
import type { SupportedLanguage } from "@/i18n";
import type { MeasureUnit } from "@/lib/measurementConverter";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const WELCOME_DISMISSED_PREFIX = "onboarding_welcome_dismissed_v1_";
const TOUR_COMPLETED_PREFIX = "onboarding_tour_completed_v1_";
// Set once the quiz is "done" AND the welcome/tour step has been resolved (started or
// skipped) — other screens (e.g. Inicio.tsx's "AI restored" modal) read this to know
// the whole onboarding sequence is out of the way before showing their own one-time UI.
const ONBOARDING_RESOLVED_PREFIX = "onboarding_fully_resolved_v1_";

// Fix B: GlucoPhoto product IDs — tour skips /glucophoto step for non-owners
const GLUCOPHOTO_PRODUCT_IDS = ["7330458", "7468894", "7487694"];

interface SpotlightRect {
  x: number;
  y: number;
  w: number;
  h: number;
  rx: number;
}

interface TourStepDef {
  target: string;
  title: string;
  description: string;
  navigateTo?: string;
  scrollToTarget?: boolean;
}

// ─────────────────────────────────────────────────────────────
// QUIZ STEP DEFINITIONS
// ─────────────────────────────────────────────────────────────
interface QuizOption {
  value: string;
  emoji: string;
  labelKey: string;
}

interface QuizStep {
  id: "name" | "language" | "measure_unit" | "goal" | "restriction" | "cooking_time";
  type: "options" | "text";
  titleKey: string;
  subtitleKey: string;
  placeholderKey?: string;
  options?: QuizOption[];
  // Lets the user pick more than one option (e.g. gluten free + lactose
  // intolerant at once). Stored as a comma-joined string in `answers`,
  // same shape as every other step, so onComplete/onStepAnswered don't
  // need to change.
  multiSelect?: boolean;
}

const QUIZ_STEPS: QuizStep[] = [
  {
    id: "language",
    type: "options",
    titleKey: "onboarding.quiz_language_title",
    subtitleKey: "onboarding.quiz_language_subtitle",
    options: [
      { value: "en", emoji: "🇺🇸", labelKey: "onboarding.quiz_lang_en" },
      { value: "es", emoji: "🇲🇽", labelKey: "onboarding.quiz_lang_es" },
      { value: "de", emoji: "🇩🇪", labelKey: "onboarding.quiz_lang_de" },
    ],
  },
  {
    id: "name",
    type: "text",
    titleKey: "onboarding.quiz_name_title",
    subtitleKey: "onboarding.quiz_name_subtitle",
    placeholderKey: "onboarding.quiz_name_placeholder",
  },
  {
    id: "measure_unit",
    type: "options",
    titleKey: "onboarding.quiz_measure_title",
    subtitleKey: "onboarding.quiz_measure_subtitle",
    options: [
      { value: "metric", emoji: "⚖️", labelKey: "onboarding.quiz_unit_metric" },
      { value: "cups", emoji: "🥤", labelKey: "onboarding.quiz_unit_cups" },
      { value: "ounces", emoji: "🫙", labelKey: "onboarding.quiz_unit_ounces" },
    ],
  },
  {
    id: "goal",
    type: "options",
    titleKey: "onboarding.quiz_goal_title",
    subtitleKey: "onboarding.quiz_goal_subtitle",
    options: [
      { value: "enjoy_food", emoji: "🍰", labelKey: "onboarding.quiz_goal_enjoy" },
      { value: "glucose",    emoji: "🩺", labelKey: "onboarding.quiz_goal_glucose" },
      { value: "weight",     emoji: "⚖️", labelKey: "onboarding.quiz_goal_weight" },
      { value: "healthy",    emoji: "🥗", labelKey: "onboarding.quiz_goal_healthy" },
    ],
  },
  {
    id: "restriction",
    type: "options",
    multiSelect: true,
    titleKey: "onboarding.quiz_restriction_title",
    subtitleKey: "onboarding.quiz_restriction_subtitle",
    options: [
      { value: "gluten_free",    emoji: "🌾", labelKey: "onboarding.quiz_restriction_gluten" },
      { value: "lactose_free",   emoji: "🥛", labelKey: "onboarding.quiz_restriction_lactose" },
      { value: "vegetarian",     emoji: "🥦", labelKey: "onboarding.quiz_restriction_vegetarian" },
      { value: "none",           emoji: "✅", labelKey: "onboarding.quiz_restriction_none" },
    ],
  },
  {
    id: "cooking_time",
    type: "options",
    titleKey: "onboarding.quiz_time_title",
    subtitleKey: "onboarding.quiz_time_subtitle",
    options: [
      { value: "15min",  emoji: "⚡", labelKey: "onboarding.quiz_time_15" },
      { value: "30min",  emoji: "🕐", labelKey: "onboarding.quiz_time_30" },
      { value: "60min",  emoji: "👨‍🍳", labelKey: "onboarding.quiz_time_60" },
    ],
  },
];

// Used when the user already completed the full quiz but never gave a name
// (e.g. onboarding_completed_at is set but display_name is still null).
const NAME_ONLY_STEPS: QuizStep[] = QUIZ_STEPS.filter((s) => s.id === "name");

// ─────────────────────────────────────────────────────────────
// QUIZ COMPONENT
// ─────────────────────────────────────────────────────────────
interface QuizProps {
  steps: QuizStep[];
  onComplete: (prefs: Record<string, string>) => void;
  onSkip: () => void;
  onStepAnswered?: (stepId: string, value: string) => void;
}

const OnboardingQuiz = ({ steps, onComplete, onSkip, onStepAnswered }: QuizProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  // Only for the overlay's one-time entrance — never touched again after mount,
  // so the overlay itself never goes back to opacity-0 between questions.
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setFadeIn(true));
  }, []);

  // Reset selected when step changes
  useEffect(() => {
    const stepDef = steps[step];
    const stored = answers[stepDef.id] ?? "";
    if (stepDef.multiSelect) {
      setSelectedMulti(stored ? stored.split(",") : []);
    } else {
      setSelected(stored || null);
    }
  }, [step]);

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;
  const isTextStep = currentStep.type === "text";
  const canAdvance = isTextStep ? true : currentStep.multiSelect ? selectedMulti.length > 0 : !!selected;

  const handleSelect = (value: string) => {
    setSelected(value);
  };

  // "None" is mutually exclusive with every other option — value is always
  // the canonical "none" regardless of UI language, only the label is
  // translated (see quiz_restriction_none), so this check works in ES/DE too.
  const handleToggleMulti = (value: string) => {
    setSelectedMulti((prev) => {
      if (value === "none") {
        return prev.includes("none") ? [] : ["none"];
      }
      const withoutNone = prev.filter((v) => v !== "none");
      return withoutNone.includes(value)
        ? withoutNone.filter((v) => v !== value)
        : [...withoutNone, value];
    });
  };

  const handleNext = () => {
    if (!canAdvance) return;
    const value = currentStep.multiSelect ? selectedMulti.join(",") : (selected ?? "").trim();
    const newAnswers = { ...answers, [currentStep.id]: value };
    setAnswers(newAnswers);

    if (value) onStepAnswered?.(currentStep.id, value);

    if (isLast) {
      onComplete(newAnswers);
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[60] flex flex-col transition-opacity duration-300 ${fadeIn ? "opacity-100" : "opacity-0"}`}
      style={{ backgroundColor: "hsl(35 40% 97%)" }}
    >
      {/* Progress bar */}
      <div className="flex gap-1.5 px-6 pt-12 pb-2 justify-center">
        {steps.map((_, i) => (
          <div
            key={i}
            className="h-1.5 flex-1 max-w-[60px] rounded-full transition-all duration-400"
            style={{
              backgroundColor: i <= step ? "hsl(30 55% 50%)" : "hsl(30 20% 85%)",
            }}
          />
        ))}
      </div>

      {/* Content — keyed by step so each question slides/fades in on its own,
          without ever hiding the overlay behind it */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6">
        <div key={step} className="w-full max-w-sm animate-fade-in">
          {/* Step counter */}
          <p className="text-center text-xs font-medium mb-3" style={{ color: "hsl(30 40% 60%)" }}>
            {step + 1} / {steps.length}
          </p>

          {/* Question */}
          <h2
            className="text-2xl font-extrabold text-center mb-1.5"
            style={{ fontFamily: "Quicksand, sans-serif", color: "hsl(30 30% 20%)" }}
          >
            {t(currentStep.titleKey)}
          </h2>
          <p className="text-center text-sm text-muted-foreground mb-7">
            {t(currentStep.subtitleKey)}
          </p>

          {isTextStep ? (
            <input
              type="text"
              value={selected ?? ""}
              onChange={(e) => setSelected(e.target.value)}
              placeholder={currentStep.placeholderKey ? t(currentStep.placeholderKey) : ""}
              autoFocus
              className="w-full rounded-2xl px-5 py-4 text-[15px] font-semibold bg-white focus:outline-none"
              style={{
                color: "hsl(30 30% 20%)",
                border: "2px solid hsl(30 20% 92%)",
                boxShadow: "0 2px 8px -2px hsl(30 20% 50% / 0.12)",
              }}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {(currentStep.options ?? []).map((opt) => {
                const isSelected = currentStep.multiSelect
                  ? selectedMulti.includes(opt.value)
                  : selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => (currentStep.multiSelect ? handleToggleMulti(opt.value) : handleSelect(opt.value))}
                    className="flex items-center gap-4 w-full rounded-2xl px-5 py-4 text-left transition-all duration-200 active:scale-[0.98]"
                    style={{
                      backgroundColor: isSelected ? "hsl(30 55% 50%)" : "white",
                      color: isSelected ? "white" : "hsl(30 30% 20%)",
                      boxShadow: isSelected
                        ? "0 6px 20px -4px hsl(30 60% 45% / 0.4)"
                        : "0 2px 8px -2px hsl(30 20% 50% / 0.12)",
                      border: isSelected ? "2px solid transparent" : "2px solid hsl(30 20% 92%)",
                    }}
                  >
                    <span className="text-2xl leading-none">{opt.emoji}</span>
                    <span className="text-[15px] font-semibold">{t(opt.labelKey)}</span>
                    {isSelected && (
                      <span className="ml-auto text-white opacity-90">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer button */}
      <div className="px-6 pb-10">
        <button
          onClick={handleNext}
          disabled={!canAdvance}
          className="w-full rounded-full py-4 text-base font-bold transition-all duration-200 active:scale-[0.97]"
          style={{
            background: canAdvance
              ? "linear-gradient(135deg, hsl(30 55% 55%), hsl(30 60% 45%))"
              : "hsl(30 15% 88%)",
            color: canAdvance ? "white" : "hsl(30 15% 60%)",
            boxShadow: canAdvance ? "0 6px 20px -4px hsl(30 60% 45% / 0.4)" : "none",
          }}
        >
          {isLast ? t("onboarding.quiz_start") : t("onboarding.quiz_next")}
        </button>
        <button
          onClick={onSkip}
          className="w-full mt-3 py-2 text-sm font-medium text-center transition-colors active:scale-[0.97]"
          style={{ color: "hsl(30 20% 55%)" }}
        >
          {t("onboarding.skip", "Skip")}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN ONBOARDING COMPONENT
// ─────────────────────────────────────────────────────────────
const OnboardingExperience = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasProduct } = useAuth();
  const { setLanguage } = useLocale();
  const { setUnit: setMeasureUnit } = usePreferredMeasureUnit();
  const userId = user?.id;
  const ownsGlucoPhoto = GLUCOPHOTO_PRODUCT_IDS.some((id) => hasProduct(id));

  const welcomeKey = userId ? `${WELCOME_DISMISSED_PREFIX}${userId}` : null;
  const tourCompletedKey = userId ? `${TOUR_COMPLETED_PREFIX}${userId}` : null;
  const resolvedKey = userId ? `${ONBOARDING_RESOLVED_PREFIX}${userId}` : null;

  // Quiz state — fetched directly from DB, decoupled from auth flow.
  // "unknown"   = not fetched yet
  // "full"      = onboarding_completed_at is null → show the whole quiz (5 questions)
  // "name_only" = onboarding_completed_at is set but display_name is empty → show just the name question
  // "done"      = nothing left to ask
  const [quizStatus, setQuizStatus] = useState<"unknown" | "full" | "name_only" | "done">("unknown");

  useEffect(() => {
    if (!userId) return;
    supabase
      .from("app_users")
      .select("onboarding_completed_at, display_name")
      .eq("id", userId)
      .single()
      .then(({ data }) => {
        const completedAt = (data as any)?.onboarding_completed_at;
        const displayName = (data as any)?.display_name;
        if (!completedAt) {
          setQuizStatus("full");
        } else if (!displayName) {
          setQuizStatus("name_only");
        } else {
          setQuizStatus("done");
        }
      });
  }, [userId]);

  // Tour states (unchanged from original)
  const [showWelcome, setShowWelcome] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [fadeIn, setFadeIn] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const tourStartingRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const padding = 10;

  const tourSteps: TourStepDef[] = [
    {
      target: "home",
      title: t("onboarding.step_home", "Welcome to Glucofy"),
      description: t("onboarding.step_home_desc", "Your central hub for recipes, content, and tools designed for a healthier life."),
      navigateTo: "/home",
    },
    {
      target: "glucophoto",
      title: t("onboarding.step_glucophoto", "GlucoPhoto"),
      description: t("onboarding.step_glucophoto_desc", "Take a photo of your meal and get an instant AI analysis of its glycemic impact, with improvement suggestions."),
      navigateTo: "/glucophoto",
    },
    {
      target: "recipes",
      title: t("onboarding.step_recipes", "Recipes"),
      description: t("onboarding.step_recipes_desc", "Discover hundreds of recipes organized by category, time and glycemic profile."),
      navigateTo: "/recipes",
    },
    {
      target: "recipes",
      title: t("onboarding.step_creator", "Recipe Creator"),
      description: t("onboarding.step_creator_desc", "Generate custom recipe ideas with AI — from ingredients you have or as a surprise."),
      navigateTo: "/recipes",
    },
    {
      target: "more",
      title: t("onboarding.step_more", "More"),
      description: t("onboarding.step_more_desc", "Access your profile, saved recipes, grocery list, support, feedback, and more."),
      navigateTo: "/more",
    },
  ].filter((s) => s.target !== "glucophoto" || ownsGlucoPhoto);

  // ── Auto-show welcome only after quiz phase is resolved ──
  useEffect(() => {
    if (!welcomeKey || !tourCompletedKey) return;
    if (quizStatus !== "done") return;
    const dismissed = localStorage.getItem(welcomeKey) === "true";
    const completed = localStorage.getItem(tourCompletedKey) === "true";
    if (!dismissed && !completed) {
      setShowWelcome(true);
      requestAnimationFrame(() => setFadeIn(true));
    }
  }, [welcomeKey, tourCompletedKey, quizStatus]);

  // ── Signal other screens once the whole onboarding sequence is out of the way ──
  // (quiz done + welcome/tour either dismissed without starting, or the tour ended).
  useEffect(() => {
    if (!resolvedKey || !welcomeKey) return;
    if (quizStatus !== "done") return;
    if (showWelcome || isTourActive) return;
    if (localStorage.getItem(welcomeKey) === "true") {
      localStorage.setItem(resolvedKey, "true");
    }
  }, [resolvedKey, welcomeKey, quizStatus, showWelcome, isTourActive]);

  // ── Central start function ──
  const startGuidedTour = useCallback(() => {
    if (tourStartingRef.current || isTourActive) return;
    tourStartingRef.current = true;

    setShowWelcome(false);
    setCardVisible(false);
    setSpotlight(null);

    if (welcomeKey) localStorage.setItem(welcomeKey, "true");

    setIsTourActive(true);
    setCurrentStep(0);
    setFadeIn(true);

    const firstStep = tourSteps[0];
    if (firstStep.navigateTo && location.pathname !== firstStep.navigateTo) {
      navigate(firstStep.navigateTo);
    }

    tourStartingRef.current = false;
  }, [isTourActive, welcomeKey, location.pathname, navigate]);

  // ── Listen for manual tutorial trigger (from More page) ──
  useEffect(() => {
    const handler = () => startGuidedTour();
    window.addEventListener("start-app-tutorial", handler);
    return () => window.removeEventListener("start-app-tutorial", handler);
  }, [startGuidedTour]);

  // ── Dismiss welcome (skip) ──
  const dismissWelcome = useCallback(() => {
    setFadeIn(false);
    if (welcomeKey) localStorage.setItem(welcomeKey, "true");
    setTimeout(() => setShowWelcome(false), 300);
  }, [welcomeKey]);

  // ── End tour ──
  const endGuidedTour = useCallback((completed: boolean) => {
    setFadeIn(false);
    if (completed && tourCompletedKey) {
      localStorage.setItem(tourCompletedKey, "true");
    }
    setTimeout(() => {
      setIsTourActive(false);
      setCurrentStep(0);
      setCardVisible(false);
      setSpotlight(null);
      if (location.pathname !== "/home") navigate("/home");
    }, 300);
  }, [tourCompletedKey, currentStep, location.pathname, navigate]);

  // ── Measure spotlight target ──
  const measureTarget = useCallback((targetId: string, shouldScroll?: boolean) => {
    const el = document.querySelector(`[data-onboarding="${targetId}"]`);
    if (!el) {
      setSpotlight(null);
      return;
    }
    if (shouldScroll) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const rect = el.getBoundingClientRect();
    const rx = Math.min(rect.width, rect.height) * 0.25;
    setSpotlight({
      x: rect.left - padding,
      y: rect.top - padding,
      w: rect.width + padding * 2,
      h: rect.height + padding * 2,
      rx,
    });
  }, []);

  // ── Update spotlight when tour step changes ──
  useEffect(() => {
    if (!isTourActive) return;
    const step = tourSteps[currentStep];

    if (step.navigateTo && location.pathname !== step.navigateTo) {
      navigate(step.navigateTo);
      const timer = setTimeout(() => measureTarget(step.target, step.scrollToTarget), 800);
      const showCard = setTimeout(() => {
        setCardVisible(true);
        setTransitioning(false);
      }, 1000);
      return () => { clearTimeout(timer); clearTimeout(showCard); };
    }

    const measureTimer = setTimeout(() => measureTarget(step.target, step.scrollToTarget), 300);
    const showCard = setTimeout(() => {
      setCardVisible(true);
      setTransitioning(false);
    }, 500);

    const handleResize = () => measureTarget(step.target);
    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(measureTimer);
      clearTimeout(showCard);
      window.removeEventListener("resize", handleResize);
    };
  }, [isTourActive, currentStep, measureTarget, location.pathname]);

  // ── Next step ──
  const nextStep = () => {
    if (transitioning) return;
    if (currentStep < tourSteps.length - 1) {
      setTransitioning(true);
      setCardVisible(false);
      setSpotlight(null);
      setTimeout(() => setCurrentStep((s) => s + 1), 250);
    } else {
      endGuidedTour(true);
    }
  };

  // ── Card positioning ──
  const getCardStyle = (): React.CSSProperties => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (!spotlight) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    const targetCenterY = spotlight.y + spotlight.h / 2;
    if (targetCenterY > vh * 0.5) {
      return { bottom: `${vh - spotlight.y + 16}px`, left: "50%", transform: "translateX(-50%)", width: "280px" };
    }
    return { top: `${spotlight.y + spotlight.h + 16}px`, left: "50%", transform: "translateX(-50%)", width: "280px" };
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER DECISION
  // ─────────────────────────────────────────────────────────────

  // Quiz — full 5-question version when onboarding was never completed, or just
  // the name question when it was completed but display_name is still empty.
  if (quizStatus === "full" || quizStatus === "name_only") {
    return (
      <OnboardingQuiz
        steps={quizStatus === "full" ? QUIZ_STEPS : NAME_ONLY_STEPS}
        onStepAnswered={(stepId, value) => {
          // setLanguage always marks the choice as manual (app_language_user_selected
          // = "true"), which is what stops useAutoLocale from reverting it right back
          // for EN-only/DE-only product owners on the very next render.
          if (stepId === "language") {
            setLanguage(value as SupportedLanguage);
          }
        }}
        onComplete={async (prefs) => {
          const language = prefs.language as SupportedLanguage | undefined;
          const measureUnit = prefs.measure_unit as MeasureUnit | undefined;
          const name = prefs.name?.trim();

          const updatePayload: Record<string, any> = {};
          if (quizStatus === "full") {
            updatePayload.onboarding_completed_at = new Date().toISOString();
            updatePayload.onboarding_preferences = prefs;
            if (language) updatePayload.preferred_language = language;
            if (measureUnit) updatePayload.preferred_measure_unit = measureUnit;
          }
          if (name) updatePayload.display_name = name;

          if (Object.keys(updatePayload).length > 0) {
            const { error } = await supabase
              .from("app_users")
              .update(updatePayload)
              .eq("id", userId!);

            if (error) {
              console.error("[OnboardingExperience] Failed to save onboarding preferences:", error);
              toast.error(t("onboarding.quiz_save_error", "Failed to save preferences. Please try again."));
              return; // keep the quiz open so the user can retry
            }
          }

          if (measureUnit) setMeasureUnit(measureUnit);
          setQuizStatus("done");
        }}
        onSkip={async () => {
          // In name_only mode, onboarding_completed_at is already set — skipping
          // the name question means there's nothing left to persist.
          if (quizStatus === "full") {
            const { error } = await supabase
              .from("app_users")
              .update({ onboarding_completed_at: new Date().toISOString() } as any)
              .eq("id", userId!);

            if (error) {
              console.error("[OnboardingExperience] Failed to mark onboarding as skipped:", error);
              toast.error(t("onboarding.quiz_save_error", "Failed to save preferences. Please try again."));
              return;
            }
          }
          setQuizStatus("done");
        }}
      />
    );
  }

  // ═══ EXISTING WELCOME + TOUR (unchanged) ═══

  // Welcome popup
  if (showWelcome && !isTourActive) {
    return (
      <div
        className={`fixed inset-0 z-[60] flex items-center justify-center transition-opacity duration-500 ${fadeIn ? "opacity-100" : "opacity-0"}`}
        style={{ backgroundColor: "hsl(35 40% 97%)" }}
      >
        <div className="w-full max-w-sm px-6 flex flex-col items-center text-center">
          <div
            className="w-48 h-48 rounded-3xl overflow-hidden shadow-xl mb-8"
            style={{ boxShadow: "0 12px 40px -8px hsl(30 50% 40% / 0.25)" }}
          >
            <img
              src={natashaImg}
              alt="Chef Natasha"
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 15%" }}
            />
          </div>

          <h1 className="text-3xl font-extrabold text-foreground mb-3" style={{ fontFamily: "Quicksand, sans-serif" }}>
            {t("onboarding.welcome")}
          </h1>

          <p className="text-muted-foreground text-[15px] leading-relaxed mb-10 max-w-[280px]">
            {t("onboarding.welcome_desc")}
          </p>

          <button
            onClick={startGuidedTour}
            className="w-full rounded-full py-3.5 text-base font-bold shadow-lg active:scale-[0.97] transition-transform"
            style={{
              background: "linear-gradient(135deg, hsl(30 55% 55%), hsl(30 60% 45%))",
              color: "white",
              boxShadow: "0 6px 20px -4px hsl(30 60% 45% / 0.4)",
            }}
          >
            {t("onboarding.start_tour", "Take the tour")}
          </button>

          <button
            onClick={dismissWelcome}
            className="mt-4 text-sm font-medium transition-colors active:scale-[0.97]"
            style={{ color: "hsl(30 20% 55%)" }}
          >
            {t("onboarding.skip", "Skip")}
          </button>
        </div>
      </div>
    );
  }

  // Tour overlay
  if (!isTourActive) return null;

  const step = tourSteps[currentStep];
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  return (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[60] transition-opacity duration-300 ${fadeIn ? "opacity-100" : "opacity-0"}`}
      onClick={nextStep}
    >
      <svg className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width={vw} height={vh} fill="white" />
            {spotlight && (
              <rect
                x={spotlight.x}
                y={spotlight.y}
                width={spotlight.w}
                height={spotlight.h}
                rx={spotlight.rx}
                ry={spotlight.rx}
                fill="black"
                style={{ transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)" }}
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width={vw} height={vh}
          fill="rgba(0,0,0,0.6)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      <div
        className="fixed z-10 rounded-2xl p-5 shadow-2xl"
        style={{
          ...getCardStyle(),
          backgroundColor: "hsl(35 40% 97%)",
          boxShadow: "0 12px 40px -8px hsl(30 50% 40% / 0.25)",
          opacity: cardVisible ? 1 : 0,
          transform: `${cardVisible ? "translateX(-50%)" : "translateX(-50%) translateY(8px)"}`,
          transition: "opacity 0.3s ease, transform 0.3s ease",
          pointerEvents: cardVisible ? "auto" : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-foreground mb-1.5">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{step.description}</p>

        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {tourSteps.map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: i === currentStep ? "hsl(30 55% 50%)" : "hsl(30 20% 80%)",
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => endGuidedTour(false)}
              className="text-xs text-muted-foreground font-medium"
            >
              {t("onboarding.skip", "Skip")}
            </button>
            <button
              onClick={nextStep}
              className="px-5 py-2 rounded-full text-sm font-bold active:scale-[0.95] transition-transform"
              style={{
                background: "linear-gradient(135deg, hsl(30 55% 55%), hsl(30 60% 45%))",
                color: "white",
              }}
            >
              {currentStep === tourSteps.length - 1 ? t("onboarding.finish") : t("onboarding.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingExperience;
